import Rest, { APIError } from 'helpers/Rest';
import * as ServerActions from 'actions/ServerActions';

export function createParams(options) {
  const usp = new URLSearchParams(options);
  return '?' + usp.toString();
}

const jsonOf = ({json, body}) => json ?? body;
export function createAPI(store) {
  const rest = new Rest(store);
  const {dispatch} = store;

  async function getProjects() {
    return jsonOf(await rest.get('/api/v1/projects'));
  }

  async function addProject(project, createdOfflineAt) {
    return jsonOf(await rest.post('/api/v1/projects', {project, created_at: createdOfflineAt}));
  }

  async function getProject(projectId) {
    return jsonOf(await rest.get(`/api/v1/projects/${projectId}`));
  }

  async function updateProject(project) {
    return jsonOf(await rest.patch(`/api/v1/projects/${project.id}`, {project}));
  }

  async function deleteProject(projectId) {
    return jsonOf(await rest.delete(`/api/v1/projects/${projectId}`));
  }

  async function getPublicProjects() {
    const response = await rest.get('/api/v1/public_projects');
    return jsonOf(response);
  }

  async function addRegistration(user) {
    return jsonOf(await rest.post('/api/v1/signups', {user}));
  }

  async function updateUser(user) {
    return jsonOf(await rest.patch('/api/v1/user', {user}));
  }

  async function setUserSettings(settings) {
    return jsonOf(await rest.patch('/api/v1/user', {user: {settings}}));
  }

  async function deleteUser() {
    return jsonOf(await rest.delete('/api/v1/user'));
  }

  async function updateDestroyRequests({email, token}) {
    const response = await rest.patch('/api/v1/destroy_requests', {email, token});
  }

  async function addAcceptance(userId, newsletter) {
    return jsonOf(await rest.patch(`/api/v1/signups/${userId}`, {user: {newsletter}}));
  }

  function initializeAppData() {
    const userPromise = getCurrentUser().then((user) => {
      dispatch(ServerActions.receivedCurrentUser(user));
    }, (e) => {
      if (e instanceof APIError) {
        console.warn(e, e.response);
      }
    });
    const productTypesListPromise = getProductTypes().then((productTypesList) => {
      dispatch(ServerActions.receivedProductTypes(productTypesList));
    }, (e) => {
      if (e instanceof APIError) {
        console.warn(e);
      }
    })
    const pushPierProductsListPromise = getPushPierProducts().then((pushPierProductsList) => {
      dispatch(ServerActions.receivedPushPierProducts(pushPierProductsList));
    }, (e) => {
      if (e instanceof APIError) {
        console.warn(e);
      }
    });

    const ramProductsListPromise = getRamProducts().then((ramProductsList) => {
      dispatch(ServerActions.receivedRamProducts(ramProductsList));
    }, (e) => {
      if (e instanceof APIError) {
        console.warn(e);
      }
    });

    return Promise.all([userPromise, productTypesListPromise, pushPierProductsListPromise, ramProductsListPromise]);
  }

  async function getProducts(filters) {
    return jsonOf(await rest.get(`/api/v1/products/helical${createParams(filters)}`));
  }

  async function getPushPierProducts() {
    return jsonOf(await rest.get(`/api/v1/products/push_pier`));
  }

  async function getRamProducts() {
    return jsonOf(await rest.get(`/api/v1/products/ram`));
  }

  async function getProductTypes() {
    return jsonOf(await rest.get(`/api/v1/product_types`));
  }

  async function getCurrentUser() {
    return jsonOf(await rest.get('/api/v1/user'));
  }

  async function downloadReport({id, markup, cut_sheet_pages}) {
    return jsonOf(await rest.post(`/api/v1/projects/${id}/report`, {markup, cut_sheet_pages}));
  }

  async function downloadLogsReport({id, markup}) {
    return jsonOf(await rest.post(`/api/v1/projects/${id}/logs_report`, {markup}));
  }

  async function getAuthToken(username, password) {
    const response = await rest.post('/oauth/token', {grant_type: 'password', username, password});
    return jsonOf(response);
  }

  async function requestDestroyUser(email) {
    return jsonOf(await rest.post('/api/v1/destroy_requests', { email }));
  }

  async function createPasswordReset(email) {
    return jsonOf(await rest.post('/api/v1/password_resets', {password_reset: {email}}));
  }

  async function changePassword(new_password, authorization_token, email) {
    return jsonOf(await rest.post('/api/v1/passwords', {password: {new_password, authorization_token, email}}));
  }

  return {
    getProjects,
    addProject,
    getProject,
    updateProject,
    deleteProject,
    getPublicProjects,
    addRegistration,
    updateUser,
    setUserSettings,
    deleteUser,
    updateDestroyRequests,
    createPasswordReset,
    changePassword,
    addAcceptance,
    initializeAppData,
    getProducts,
    getPushPierProducts,
    getRamProducts,
    getProductTypes,
    getCurrentUser,
    downloadReport,
    downloadLogsReport,
    getAuthToken,
    requestDestroyUser,
  };
}
